import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_KEY = 'duc_cookie_consent';

function getStoredConsent(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(COOKIE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (preferences: CookiePreferences) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString(),
    }));
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => saveConsent(prefs);

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Overlay when details open */}
          {showDetails && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-[9998]"
              onClick={() => setShowDetails(false)}
            />
          )}

          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-[480px] z-[9999]"
          >
            {!showDetails ? (
              /* Simple Banner */
              <div className="bg-white rounded-2xl border border-[#0A1628]/10 overflow-hidden" style={{ boxShadow: '0 8px 40px rgba(10,22,40,0.14)' }}>
                {/* Top accent line */}
                <div className="h-1 bg-gradient-to-r from-[#3B9EE8] to-[#2D85C5]" />
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/10 flex-shrink-0 mt-0.5">
                      <i className="ri-cookie-line text-[#3B9EE8] text-xl" />
                    </div>
                    <div>
                      <h3 className="font-heading font-700 text-[#0A1628] text-base mb-1">
                        Wij gebruiken cookies
                      </h3>
                      <p className="text-[#0A1628]/60 text-sm leading-relaxed">
                        We gebruiken cookies om onze website te verbeteren en relevante content te tonen.
                        Lees ons{' '}
                        <Link to="/cookiebeleid" className="text-[#3B9EE8] hover:underline">cookiebeleid</Link>
                        {' '}voor meer informatie.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <div className="flex items-center gap-1.5 bg-[#3B9EE8]/8 rounded-full px-3 py-1">
                      <i className="ri-shield-check-line text-[#3B9EE8] text-xs" />
                      <span className="text-[#3B9EE8] text-xs font-500">Noodzakelijk</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-[#0A1628]/5 rounded-full px-3 py-1">
                      <i className="ri-bar-chart-line text-[#0A1628]/50 text-xs" />
                      <span className="text-[#0A1628]/50 text-xs font-500">Analytisch</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-[#0A1628]/5 rounded-full px-3 py-1">
                      <i className="ri-advertisement-line text-[#0A1628]/50 text-xs" />
                      <span className="text-[#0A1628]/50 text-xs font-500">Marketing</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={acceptAll}
                      className="flex-1 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-sm py-2.5 px-5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                    >
                      Alles accepteren
                    </button>
                    <button
                      onClick={rejectAll}
                      className="flex-1 bg-[#F7F8FA] hover:bg-[#0A1628]/8 text-[#0A1628]/70 font-600 text-sm py-2.5 px-5 rounded-lg transition-all cursor-pointer whitespace-nowrap border border-[#0A1628]/8"
                    >
                      Alleen noodzakelijk
                    </button>
                    <button
                      onClick={() => setShowDetails(true)}
                      className="text-[#0A1628]/45 hover:text-[#3B9EE8] text-xs font-500 px-3 py-2.5 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Aanpassen
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Detailed Settings */
              <div className="bg-white rounded-2xl border border-[#0A1628]/10 overflow-hidden" style={{ boxShadow: '0 8px 40px rgba(10,22,40,0.14)' }}>
                <div className="h-1 bg-gradient-to-r from-[#3B9EE8] to-[#2D85C5]" />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-heading font-700 text-[#0A1628] text-base">Cookie instellingen</h3>
                    <button
                      onClick={() => setShowDetails(false)}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#0A1628]/6 text-[#0A1628]/50 hover:text-[#0A1628] transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-base" />
                    </button>
                  </div>

                  <div className="space-y-3 mb-5">
                    {/* Necessary */}
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-[#3B9EE8]/5 border border-[#3B9EE8]/15">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B9EE8]/15 flex-shrink-0 mt-0.5">
                        <i className="ri-shield-check-line text-[#3B9EE8] text-sm" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-600 text-[#0A1628] text-sm">Noodzakelijke cookies</span>
                          <span className="text-[#3B9EE8] text-xs font-500 bg-[#3B9EE8]/10 px-2 py-0.5 rounded-full">Altijd aan</span>
                        </div>
                        <p className="text-[#0A1628]/55 text-xs leading-relaxed">Vereist voor de basisfunctionaliteit van de website (sessie, formulieren, beveiliging).</p>
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F7F8FA] border border-[#0A1628]/8">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A1628]/8 flex-shrink-0 mt-0.5">
                        <i className="ri-bar-chart-line text-[#0A1628]/60 text-sm" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-600 text-[#0A1628] text-sm">Analytische cookies</span>
                          <button
                            onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                            className={`relative w-10 h-5 rounded-full transition-all duration-200 cursor-pointer flex-shrink-0 ${prefs.analytics ? 'bg-[#3B9EE8]' : 'bg-[#0A1628]/20'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-200 ${prefs.analytics ? 'left-5' : 'left-0.5'}`} />
                          </button>
                        </div>
                        <p className="text-[#0A1628]/55 text-xs leading-relaxed">Helpt ons begrijpen hoe bezoekers de site gebruiken (bijv. Google Analytics). Volledig geanonimiseerd.</p>
                      </div>
                    </div>

                    {/* Marketing */}
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F7F8FA] border border-[#0A1628]/8">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A1628]/8 flex-shrink-0 mt-0.5">
                        <i className="ri-advertisement-line text-[#0A1628]/60 text-sm" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-600 text-[#0A1628] text-sm">Marketing cookies</span>
                          <button
                            onClick={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))}
                            className={`relative w-10 h-5 rounded-full transition-all duration-200 cursor-pointer flex-shrink-0 ${prefs.marketing ? 'bg-[#3B9EE8]' : 'bg-[#0A1628]/20'}`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-200 ${prefs.marketing ? 'left-5' : 'left-0.5'}`} />
                          </button>
                        </div>
                        <p className="text-[#0A1628]/55 text-xs leading-relaxed">Voor gepersonaliseerde advertenties op sociale media en Google (bijv. retargeting).</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={saveCustom}
                      className="flex-1 bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-sm py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                    >
                      Opslaan
                    </button>
                    <button
                      onClick={acceptAll}
                      className="flex-1 bg-[#F7F8FA] hover:bg-[#0A1628]/8 text-[#0A1628]/70 font-600 text-sm py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap border border-[#0A1628]/8"
                    >
                      Alles accepteren
                    </button>
                  </div>

                  <p className="text-center text-[#0A1628]/35 text-xs mt-3">
                    AVG/GDPR-compliant &bull; <Link to="/privacybeleid" className="hover:text-[#3B9EE8] transition-colors">Privacybeleid</Link> &bull; <Link to="/cookiebeleid" className="hover:text-[#3B9EE8] transition-colors">Cookiebeleid</Link>
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
